import React, { useState, useMemo, useEffect, useRef } from "react";
import { Box, Snackbar, Stack, ToggleButton, ToggleButtonGroup, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IStats,
  IManualMovement,
  IPosition,
  IDailyBotSnapshot,
  IPerformanceDataPoint,
  IBotData,
  BotConfig,
} from "../../shared/types";
import { getBotConfig, patchBotConfig } from "../../api/botConfig";
import { ReconnectingWebSocket } from "../../socket";
import { formatCompactCurrency, formatCurrency, formatUsd } from "../../utils/formatters";
import { botState, hodlBenchmarkLabel } from "../../utils/botMetrics";

import CapitalFlowCard from "./CapitalFlowCard";
import { calculateTotalVolume } from "../../features/calculateTotalVolume";
import CurrentPoolCard from "./CurrentPoolCard"; // Changed from PoolInfoCard
import MovementsDialog from "./MovementsDialog";

import PositionsTable from "./PositionsTable";
import { processPortfolioHistory } from "../../features/overviewSeries";
import DistributionPieChart from "../../components/charts/AssetDistributionPie/DistributionPieChart";
import PerformanceChart from "../../components/charts/Performance/PerformanceChart";
import VolumeChart from "../../components/charts/VolumeChart";
import { processVolumeData } from "../../features/processVolumeData";
import TotalCapitalCard from "./TotalCapitalCard";
import LPPerformanceCard from "./LPPerformanceCard";
import LPVsHodlCard from "./LPVsHodlCard";
import { useAuth } from "../../hooks/useAuth";
import { LowGasAlert } from "../../components/LowGasAlert";
import { WaitingFundsAlert } from "../../components/WaitingFundsAlert";
import { OutOfRangeAlert } from "../../components/OutOfRangeAlert";
import { BalancingStatusAlert } from "../../components/BalancingStatusAlert";
import { isWaitingForFunds } from "../../utils/botUtils";

const isUsable = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * The movement to save after an edit in the movements dialog. amountUSD follows quantity × price only
 * when one of those two was the field edited and both are finite and > 0; otherwise the edited (or
 * original) amount stands. A movement without quantity or price (the backend sends 0) would otherwise be
 * saved at $0, silently moving the capital basis and Total P&L. A cleared cell (NaN) keeps the original
 * value: no non-finite number is ever sent.
 */
const movementToSave = (edited: IManualMovement, original?: IManualMovement): IManualMovement => {
  const pick = (field: "quantity" | "price" | "amountUSD") => {
    const value = edited[field];
    if (isUsable(value)) return value;
    const before = original?.[field];
    if (isUsable(before)) return before;
    return typeof value === "number" ? undefined : value;
  };
  const quantity = pick("quantity");
  const price = pick("price");
  const pricingEdited = !!original && (quantity !== original.quantity || price !== original.price);
  const amountUSD =
    pricingEdited && isUsable(quantity) && isUsable(price) && quantity > 0 && price > 0
      ? quantity * price
      : pick("amountUSD") ?? 0;
  return { ...edited, quantity, price, amountUSD };
};

interface Props {
  stats: IStats;
  movements: IManualMovement[];
  history: IDailyBotSnapshot[];
  onEditMovement: (m: IManualMovement) => void;
  onDeleteMovement: (id: string) => Promise<void>;
  mode: "full" | "summary";
  fullPositions: IPosition[];
  onDoubleClick?: () => void;
  socket: ReconnectingWebSocket; // Add socket prop
}

const BotStatsDetailedView: React.FC<Props> = ({
  stats,
  movements,
  history,
  onEditMovement,
  onDeleteMovement,
  mode,
  fullPositions,
  onDoubleClick,
  socket, // Accept socket prop
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [botConfig, setBotConfig] = useState<BotConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const socketRef = useRef(socket);
  const { groups } = useAuth();
  const isAdmin = groups.includes("admin");

  const [volumeTimeframe, setVolumeTimeframe] = useState<
    "daily" | "weekly" | "monthly"
  >("monthly");
  const volumeData = useMemo(() => {
    return processVolumeData(fullPositions, volumeTimeframe);
  }, [fullPositions, volumeTimeframe]);

  const totalVolume = useMemo(
    () => calculateTotalVolume(fullPositions),
    [fullPositions]
  );

  const showSnackbar = (msg: string) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Same since-inception index as the overview and family heroes, so the chart's last point
  // matches the LP vs HODL card above it.
  const chartDataFromProc: IPerformanceDataPoint[] = useMemo(
    () => processPortfolioHistory([{ stats, history, positions: [], movements: [] } as unknown as IBotData]),
    [history, stats]
  );

  const chartData = useMemo(() => {
    const result = chartDataFromProc.map((pt) => ({
      ...pt,
      date: new Date(pt.date).getTime(),
    }));

    return result;
  }, [chartDataFromProc]);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    if (!stats?.botName) return;

    setLoadingConfig(true);

    const handleBotConfigResponse = (payload: any) => {
      if (payload && payload.id === stats.botName) {
        setBotConfig(payload.config);
        setLoadingConfig(false);
      }
    };

    // usamos la referencia estable
    socketRef.current?.on("botConfig", handleBotConfigResponse);

    getBotConfig(socketRef.current, stats.botName);

    const timeout = setTimeout(() => setLoadingConfig(false), 5000);

    return () => {
      socketRef.current?.off("botConfig", handleBotConfigResponse);
      clearTimeout(timeout);
    };
  }, [stats?.botName]);

  const handleForceClose = async () => {
    if (!window.confirm("Are you sure you want to force close the position?"))
      return;
    const password = window.prompt("Enter password to confirm:");
    if (!password) return;
    setLoadingAction(true);
    try {
      await patchBotConfig(
        socket,
        stats.botName,
        { forceExitOnce: true },
        password
      );
      showSnackbar("Force position close triggered");
      // Optionally refetch config
      setBotConfig({ ...(botConfig as BotConfig), forceExitOnce: true });
    } catch (e) {
      showSnackbar("Failed to force close position");
    } finally {
      setLoadingAction(false);
    }
  };

  const handlePauseResume = async (enabled: boolean) => {
    if (
      !window.confirm(
        `Are you sure you want to ${enabled ? "resume" : "pause"} the bot?`
      )
    )
      return;
    const password = window.prompt("Enter password to confirm:");
    if (!password) return;
    setLoadingAction(true);
    try {
      await patchBotConfig(socket, stats.botName, { enabled }, password);
      showSnackbar(enabled ? "Bot resumed" : "Bot paused");
      setBotConfig({ ...(botConfig as BotConfig), enabled });
    } catch (e) {
      showSnackbar("Failed to update bot status");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCloseAndPause = async () => {
    if (
      !window.confirm(
        "Are you sure you want to close the position AND pause the bot? This will exit the position immediately and disable the bot."
      )
    )
      return;
    const password = window.prompt("Enter password to confirm:");
    if (!password) return;
    setLoadingAction(true);
    try {
      await patchBotConfig(
        socket,
        stats.botName,
        { forceExitOnce: true, closeAndDisable: true },
        password
      );
      showSnackbar("Close & Pause triggered - bot will disable after closing position");
      // Update local config immediately for UI feedback
      setBotConfig({
        ...(botConfig as BotConfig),
        forceExitOnce: true,
      });
    } catch (e) {
      showSnackbar("Failed to trigger Close & Pause");
    } finally {
      setLoadingAction(false);
    }
  };

  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [tokenView, setTokenView] = useState<"wallet" | "pool" | "total">("total");
  const snap = stats.balanceSnapshotEnd;
  const token0Color = theme.palette.chart.gold;
  const token1Color = theme.palette.chart.green;

  const walletData = [
    {
      name: stats.token0Symbol,
      value: snap.walletToken0 * snap.token0Price,
      amount: snap.walletToken0,
      usdValue: snap.walletToken0 * snap.token0Price,
      color: token0Color,
    },
    {
      name: stats.token1Symbol,
      value: snap.walletToken1 * snap.token1Price,
      amount: snap.walletToken1,
      usdValue: snap.walletToken1 * snap.token1Price,
      color: token1Color,
    },
  ];

  const poolData = [
    {
      name: stats.token0Symbol,
      value: snap.poolToken0 * snap.token0Price,
      amount: snap.poolToken0,
      usdValue: snap.poolToken0 * snap.token0Price,
      color: token0Color,
    },
    {
      name: stats.token1Symbol,
      value: snap.poolToken1 * snap.token1Price,
      amount: snap.poolToken1,
      usdValue: snap.poolToken1 * snap.token1Price,
      color: token1Color,
    },
  ];

  // What "All tokens" covers, and what it leaves out, so its total reconciles with total capital
  // (wallet + pool + balancing + buybacks; lent is part of balancing, uncollected fees part of the pool).
  const balancingInPair =
    (stats.balancingUSD || 0) > 0 &&
    (stats.balancingTokenName === stats.token0Symbol || stats.balancingTokenName === stats.token1Symbol);
  // "$31.6K" from $1,000, "$302" below (card figures drop cents from $100).
  const noteUsd = (v: number) => (Math.abs(v) >= 1000 ? formatCompactCurrency(v) : formatUsd(v));
  const excludedParts = [
    (stats.repurchaseCurValue || 0) > 0 && `buybacks ${noteUsd(stats.repurchaseCurValue)}`,
    (stats.balancingUSD || 0) > 0 && !balancingInPair && `balancing ${noteUsd(stats.balancingUSD)}`,
  ].filter(Boolean) as string[];
  const allTokensNote = `Wallet + pool${balancingInPair ? " + balancing" : ""}${
    excludedParts.length > 0 ? `. Excludes ${excludedParts.join(", ")}.` : "."
  }`;

  const totalData = [
    {
      name: stats.token0Symbol,
      value:
        (snap.walletToken0 + snap.poolToken0) * snap.token0Price +
        (stats.balancingTokenName === stats.token0Symbol ? stats.balancingUSD : 0),
      amount:
        snap.walletToken0 +
        snap.poolToken0 +
        (stats.balancingTokenName === stats.token0Symbol
          ? stats.balancingUSD / snap.token0Price
          : 0),
      usdValue:
        (snap.walletToken0 + snap.poolToken0) * snap.token0Price +
        (stats.balancingTokenName === stats.token0Symbol ? stats.balancingUSD : 0),
      color: token0Color,
    },
    {
      name: stats.token1Symbol,
      value:
        (snap.walletToken1 + snap.poolToken1) * snap.token1Price +
        (stats.balancingTokenName === stats.token1Symbol ? stats.balancingUSD : 0),
      amount:
        snap.walletToken1 +
        snap.poolToken1 +
        (stats.balancingTokenName === stats.token1Symbol
          ? stats.balancingUSD / snap.token1Price
          : 0),
      usdValue:
        (snap.walletToken1 + snap.poolToken1) * snap.token1Price +
        (stats.balancingTokenName === stats.token1Symbol ? stats.balancingUSD : 0),
      color: token1Color,
    },
  ];

  const poolEmptyMessage =
    snap.poolValueUSD === 0
      ? stats.balancingUSD === 0
        ? "A new position will be opened in the next few minutes."
        : `No assets in the pool while balancing ${formatCurrency(stats.balancingUSD)} (${stats.balancing} ${stats.balancingTokenName}).`
      : undefined;

  const gap = { xs: 2, md: 2.5 };
  // Earning no fees (out of range, or no position while not balancing): explained before anything else.
  const state = botState(stats);
  // Operational alert (like gas and funds): admins only.
  const showRangeAlert = isAdmin && (state === "out-of-range" || state === "idle");

  return (
    <Box>
      {showRangeAlert && (
        <Box sx={{ mb: gap }}>
          <OutOfRangeAlert stats={stats} />
        </Box>
      )}

      {/* Headline KPIs */}
      <Box
        sx={{
          display: "grid",
          gap,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: isAdmin ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
          },
          "& > *:nth-of-type(3)": isAdmin ? { gridColumn: { sm: "1 / -1", md: "auto" } } : {},
        }}
      >
        <TotalCapitalCard stats={stats} decimals={stats.decimals} isAdmin={isAdmin} />
        <LPPerformanceCard stats={stats} totalVolume={totalVolume} isAdmin={isAdmin} />
        {isAdmin && <LPVsHodlCard stats={stats} decimals={stats.decimals} />}
      </Box>

      {/* Operational alerts - Admin only */}
      <Stack spacing={1.5} sx={{ mt: gap, "&:empty": { display: "none" } }}>
        <LowGasAlert
          stats={stats}
          isAdmin={isAdmin}
          recommendedAmount={botConfig?.nativeTokenMinAmountForFees}
          onCopy={(text, label) => {
            navigator.clipboard.writeText(text);
            showSnackbar(`${label} copied`);
          }}
        />

        {isWaitingForFunds(stats) && (
          <WaitingFundsAlert
            botName={stats.botName}
            stepDescription={stats.currentBalancingStep}
            isAdmin={isAdmin}
            amount={stats.waitingFundsAmount}
            token={stats.waitingFundsToken}
            chainId={stats.waitingFundsChainId}
            walletAddress={stats.waitingFundsAddress}
            binanceSubaccount={stats.binanceDepositSubaccount}
            binanceToken={stats.binanceDepositToken}
            binanceAmount={stats.binanceDepositAmount}
            onCopy={(text, label) => {
              navigator.clipboard.writeText(text);
              showSnackbar(`${label} copied`);
            }}
          />
        )}

        <BalancingStatusAlert stats={stats} isAdmin={isAdmin} />
      </Stack>

      {mode === "full" && (
        <Stack spacing={gap} sx={{ mt: gap }}>
          {/* Capital flow + current pool */}
          <Box
            sx={{
              display: "grid",
              gap,
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            }}
          >
            <CapitalFlowCard
              stats={stats}
              decimals={stats.decimals}
              onOpenMovementsDialog={() => setIsDialogOpen(true)}
            />
            <CurrentPoolCard
              stats={stats}
              decimals={stats.decimals}
              onCopy={(t, l) => {
                navigator.clipboard
                  .writeText(t)
                  .then(() => showSnackbar(`${l} copied`));
              }}
              botConfig={botConfig || undefined}
              onForceClose={handleForceClose}
              onCloseAndPause={handleCloseAndPause}
              onPauseResume={handlePauseResume}
              loadingAction={loadingAction}
              loadingConfig={loadingConfig}
              onSetLoadingConfig={setLoadingConfig}
              onSaveConfig={(updatedConfig) => {
                setBotConfig(updatedConfig);
                showSnackbar("Bot configuration updated");
              }}
              socket={socket}
            />
          </Box>

          {/* Token distribution — phones: one card with a Wallet / Pool / Total switch */}
          {chartData.length > 0 && isPhone && (
            <DistributionPieChart
              data={tokenView === "wallet" ? walletData : tokenView === "pool" ? poolData : totalData}
              title="Tokens"
              decimals={stats.decimals}
              emptyMessage={tokenView === "pool" ? poolEmptyMessage : undefined}
              infoTooltipText={tokenView === "total" ? allTokensNote : undefined}
              action={
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={tokenView}
                  onChange={(_, v: "wallet" | "pool" | "total" | null) => v && setTokenView(v)}
                  aria-label="Token view"
                >
                  <ToggleButton value="wallet">Wallet</ToggleButton>
                  <ToggleButton value="pool">Pool</ToggleButton>
                  <ToggleButton value="total">All</ToggleButton>
                </ToggleButtonGroup>
              }
            />
          )}
          {chartData.length > 0 && !isPhone && (
            <Box
              sx={{
                display: "grid",
                gap,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
              }}
            >
              <DistributionPieChart
                data={walletData}
                title="Wallet tokens"
                decimals={stats.decimals}
              />
              <DistributionPieChart
                data={poolData}
                title="Pool tokens"
                decimals={stats.decimals}
                emptyMessage={poolEmptyMessage}
              />
              <DistributionPieChart
                data={totalData}
                title="All tokens"
                infoTooltipText={allTokensNote}
                decimals={stats.decimals}
              />
            </Box>
          )}

          {/* Volume generated */}
          {volumeData.length > 0 && (
            <VolumeChart
              data={volumeData}
              timeframe={volumeTimeframe}
              title="Volume provided"
              subtitle={`${formatCompactCurrency(totalVolume)} since inception`}
              action={
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={volumeTimeframe}
                  onChange={(_, v) => v && setVolumeTimeframe(v)}
                  aria-label="Volume timeframe"
                >
                  <ToggleButton value="daily">Daily</ToggleButton>
                  <ToggleButton value="weekly">Weekly</ToggleButton>
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                </ToggleButtonGroup>
              }
            />
          )}

          {/* Net capital change LP vs HODL */}
          {isAdmin && chartData.length > 0 && (
            <PerformanceChart
              data={chartData}
              decimals={stats.decimals}
              title="LP vs HODL"
              subtitle="Cumulative P&L · % of today's capital"
              hodlLabel={`HODL · ${hodlBenchmarkLabel([stats as IBotData["stats"]])}`}
              pairTokens={[stats.token0Symbol, stats.token1Symbol]}
            />
          )}

          {/* Positions */}
          {fullPositions.length > 0 && (
            <PositionsTable
              fullPositions={fullPositions}
              decimals={stats.decimals}
            />
          )}
        </Stack>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />

      <MovementsDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        movements={movements}
        onEdit={(m) => {
          onEditMovement(movementToSave(m, movements.find((x) => x.id === m.id)));
          showSnackbar("Movement updated");
        }}
        onDelete={async (id) => {
          await onDeleteMovement(id);
          showSnackbar("Movement deleted");
        }}
      />
    </Box>
  );
};

export default BotStatsDetailedView;
